// Hook personalizado para obtener los mensajes de un chatroom específico.

import { useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRealtimeChannel } from '../useRealtimeChannel';
import { chatService } from '../../services/chatService';
import { useAuthQuery } from '../useAuthQuery';
import { supabase } from '../../lib/supabase';

// Constante para controlar logs de desarrollo
const DEBUG = process.env.NODE_ENV === 'development';

// Mapa global para rastrear inicializaciones por friendId (previene logs duplicados por re-montue)
const globalInitializedMap = new Map();

export function useChatMessages(friendId) {
  const initializedRef = useRef(null);
  const { sessionQuery } = useAuthQuery();
  const session = sessionQuery.data?.session;
  const queryClient = useQueryClient();

  // Log solo cuando realmente cambia friendId (usando mapa global para evitar logs por re-montue)
  const lastInitializedFriendId = globalInitializedMap.get(friendId);
  if (friendId && lastInitializedFriendId !== friendId) {
    if (DEBUG) console.log('useChatMessages: Hook inicializado o friendId cambiado:', friendId);
    globalInitializedMap.set(friendId, friendId);
    initializedRef.current = friendId;
  }
  
  // Referencia para debounce de invalidación
  const invalidateTimeout = useRef(null);
  const lastInvalidationTime = useRef(0);

  
  const query = useQuery({
    queryKey: ['chatMessages', friendId, session?.user?.id],
    queryFn: async () => {
      if (DEBUG) console.log('useChatMessages queryFn: Llamando getChatroomMessages con friendId:', friendId);
      const result = await chatService.getChatroomMessages(friendId, session);
      
      // Normalizar datos: truncar bodies base64 muy grandes y parsear msg_extra_data
      let processedResult = result;
      if (result && Array.isArray(result)) {
        processedResult = result.map(grupo => ({
          ...grupo,
          date_trunc: grupo.date_trunc 
        }));

        // Ordenar grupos por fecha: más antigua primero, más reciente al final (como WhatsApp)
        processedResult.sort((a, b) => new Date(a.date_trunc) - new Date(b.date_trunc));

        // Log de grupos ordenados (solo en desarrollo, sin logs individuales)
        if (DEBUG) {
          console.log('useChatMessages queryFn: Procesados y ordenados', processedResult.length, 'grupos');
        }
      }
      
      if (DEBUG) console.log('useChatMessages queryFn: Resultado procesado y ordenado:', processedResult);
      return processedResult;
    },
    enabled: !!friendId && !!session?.user?.id,
    // Optimizado para trabajar con cache del servicio y reintentos inteligentes
    staleTime: 10 * 1000, // 10 segundos para balance entre frescura y performance
    gcTime: 3 * 60 * 1000, // 3 minutos cache
    retry: 0, // Sin retry en TanStack, el servicio maneja reintentos inteligentes
  });

  // Función optimizada para invalidar cache y query con debounce
  // Evita múltiples invalidaciones en corto tiempo
  // Solo invalida la query de React Query, ya que el servicio maneja su propio cache
  const debouncedInvalidate = useCallback(() => {
    const now = Date.now();
    if (now - lastInvalidationTime.current < 500) {
      if (DEBUG) console.log('useChatMessages: Ignorando invalidación - muy reciente');
      return;
    }
    
    lastInvalidationTime.current = now;
    
    // Limpiar timeout existente
    if (invalidateTimeout.current) {
      clearTimeout(invalidateTimeout.current);
    }
    
    // Configurar nuevo timeout
    invalidateTimeout.current = setTimeout(() => {
      if (DEBUG) console.log('useChatMessages: Invalidando query con debounce');
      
      // Invalidar el cache del chatService para forzar fetch fresco
      chatService.invalidateChatCache(friendId);
      
      // Invalidar la query de React Query con refetch inmediato
      queryClient.invalidateQueries(['chatMessages', friendId, session?.user?.id], {
        refetchType: 'active'
      });
      
      if (DEBUG) {
        console.log('useChatMessages: Cache invalidado y refetch forzado');
      }
    }, 300);
  }, [friendId, session?.user?.id, queryClient]);

  // Efecto para log cuando cambia el estado enabled (solo cambios reales)
  useEffect(() => {
    const enabled = !!friendId && !!session?.user?.id;
    if (initializedRef.current && DEBUG) {
      console.log('useChatMessages: Estado enabled:', enabled);
    }
  }, [friendId, session?.user?.id]);

  // Crear un nombre de canal consistente para ambos usuarios
  // Ordenando los IDs alfabéticamente para asegurar que ambos escuchen en el mismo canal
  const sortedIds = [friendId, session?.user?.id].sort();
  const consistentChannelName = `chat_messages_${sortedIds[0]}_${sortedIds[1]}`;
  
  // Usar el hook para gestión mejorada de canales
  const { channel, subscribe, on, getChannelState, reset } = useRealtimeChannel(
    consistentChannelName,
    { 
      private: true,
      broadcast: { self: false }
    }
  );

  // Función para configurar la suscripción con chatroom específico
  const setupRealtimeSubscription = useCallback(async () => {
    try {
      if (DEBUG) {
        console.log('=== useChatMessages: INICIANDO CONFIGURACIÓN DE REALTIME ===');
        console.log('useChatMessages setupRealtimeSubscription: Iniciando configuración para friendId:', friendId);
        console.log('useChatMessages setupRealtimeSubscription: Session user ID:', session?.user?.id);
        console.log('useChatMessages setupRealtimeSubscription: Timestamp:', new Date().toISOString());
      }
      
      // Resetear canal existente para poder agregar nuevos listeners
      const channelState = getChannelState();
      if (DEBUG) {
        console.log('useChatMessages setupRealtimeSubscription: Estado actual del canal:', channelState);
        console.log('useChatMessages setupRealtimeSubscription: Canal ya suscrito:', channelState.isSubscribed);
      }
      
      if (channelState.isSubscribed) {
        if (DEBUG) {
          console.log('useChatMessages setupRealtimeSubscription: Reseteando canal existente antes de reconfigurar');
          console.log('useChatMessages setupRealtimeSubscription: Canal name:', consistentChannelName);
        }
        reset();
        
        // Pequeño delay para asegurar que el canal esté listo
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Obtener el chatroom ID para filtrar mensajes específicos
      // Importante: La consulta debe considerar ambos órdenes posibles
      const query = `or(and(remitente.eq.${session.user.id},destinatario.eq.${friendId}),and(remitente.eq.${friendId},destinatario.eq.${session.user.id}))`;
      if (DEBUG) {
        console.log('useChatMessages setupRealtimeSubscription: Query SQL:', query);
        console.log('useChatMessages setupRealtimeSubscription: session.user.id:', session.user.id);
        console.log('useChatMessages setupRealtimeSubscription: friendId:', friendId);
        console.log('useChatMessages setupRealtimeSubscription: Buscando chatroom entre:', session.user.id, '<->', friendId);
      }
      
      if (DEBUG) {
        console.log('useChatMessages setupRealtimeSubscription: Ejecutando query para buscar chatroom');
        console.log('useChatMessages setupRealtimeSubscription: Query SQL:', query);
      }
      
      const { data: chatrooms, error: chatroomError } = await supabase
        .from('chatrooms')
        .select('id')
        .or(query)
        .maybeSingle();

      if (DEBUG) {
        console.log('useChatMessages setupRealtimeSubscription: Resultado chatrooms:', chatrooms);
        console.log('useChatMessages setupRealtimeSubscription: Error chatroom:', chatroomError);
      }

      let chatroomId = null;
      if (chatrooms) {
        chatroomId = chatrooms.id;
        if (DEBUG) console.log('useChatMessages setupRealtimeSubscription: chatroom_id encontrado:', chatroomId);
      }

      // Escuchar mensajes nuevos con filtro específico del chatroom si existe
      if (chatroomId) {
        if (DEBUG) {
          console.log('=== useChatMessages: CONFIGURANDO CANAL ESPECÍFICO ===');
          console.log('useChatMessages setupRealtimeSubscription: Configurando canal específico para chatroom:', chatroomId);
          console.log('useChatMessages setupRealtimeSubscription: Filtro:', `chatroom_id=eq.${chatroomId}`);
          console.log('useChatMessages setupRealtimeSubscription: Canal name:', consistentChannelName);
        }

        on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'mensajitos',
          filter: `chatroom_id=eq.${chatroomId}`,
        }, (payload) => {
          if (DEBUG) {
            console.log('=== useChatMessages: EVENTO DETECTADO (CANAL ESPECÍFICO) ===');
            console.log('useChatMessages realtime: Nuevo mensaje insertado (chatroom):', payload);
            console.log('useChatMessages realtime: Tipo de evento:', payload.eventType);
            console.log('useChatMessages realtime: Detalles del nuevo mensaje:', {
              id: payload.new?.id,
              remitente: payload.new?.remitente,
              destinatario: payload.new?.destinatario,
              body: payload.new?.body?.substring(0, 50),
              chatroomId: chatroomId,
              mensajeChatroomId: payload.new?.chatroom_id,
              timestamp: new Date().toISOString()
            });
          }
          
          // Verificar que el mensaje pertenezca realmente a este chatroom
          const newMessage = payload.new;
          if (newMessage?.chatroom_id === chatroomId) {
            if (DEBUG) {
              console.log('✅ useChatMessages realtime: MENSAJE PERTENECE AL CHATROOM - Invalidando query');
              console.log('useChatMessages realtime: Chatroom ID coincide:', newMessage?.chatroom_id, '===', chatroomId);
            }
            // Invalidar la query usando debounce
            debouncedInvalidate();
          } else {
            if (DEBUG) {
              console.log('❌ useChatMessages realtime: Mensaje no pertenece a este chatroom:', {
                mensajeChatroomId: newMessage?.chatroom_id,
                expectedChatroomId: chatroomId,
                mensajeId: newMessage?.id,
                remitente: newMessage?.remitente,
                destinatario: newMessage?.destinatario
              });
            }
          }
        });
      } else {
        if (DEBUG) {
          console.log('=== useChatMessages: CONFIGURANDO CANAL GENÉRICO ===');
          console.log('useChatMessages setupRealtimeSubscription: No se encontró chatroom, usando canal genérico');
          console.log('useChatMessages setupRealtimeSubscription: Canal name:', consistentChannelName);
          console.log('useChatMessages setupRealtimeSubscription: Esperando mensajes entre:', session?.user?.id, '<->', friendId);
        }
        
        // Escuchar mensajes nuevos entre estos usuarios sin filtro de chatroom
        on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'mensajitos',
        }, (payload) => {
          if (DEBUG) {
            console.log('=== useChatMessages: EVENTO DETECTADO (CANAL GENÉRICO) ===');
            console.log('useChatMessages realtime: Mensaje nuevo detectado (canal genérico):', payload);
            console.log('useChatMessages realtime: Timestamp:', new Date().toISOString());
          }
          
          const newMessage = payload.new;
          
          // Debug IDs para detectar problemas de formato
          if (DEBUG) {
            console.log('useChatMessages realtime: Verificando relevancia:', {
              newMessage: {
                id: newMessage?.id,
                remitente: newMessage?.remitente,
                destinatario: newMessage?.destinatario,
                chatroom_id: newMessage?.chatroom_id
              },
              friendId: friendId,
              sessionUserId: session?.user?.id,
              remitenteEsFriendId: newMessage?.remitente === friendId,
              destinatarioEsSessionId: newMessage?.destinatario === session?.user?.id,
              remitenteEsSessionId: newMessage?.remitente === session?.user?.id,
              destinatarioEsFriendId: newMessage?.destinatario === friendId,
              timestamp: new Date().toISOString()
            });
          }
          
          // Verificar si este mensaje es relevante para este chat
          // Asegurar que todos los valores existan antes de comparar
          if (newMessage?.remitente && newMessage?.destinatario && friendId && session?.user?.id) {
            const esRelevante = (
              (newMessage.remitente === friendId && newMessage.destinatario === session.user.id) ||
              (newMessage.remitente === session.user.id && newMessage.destinatario === friendId)
            );
            
            if (DEBUG) {
              console.log('useChatMessages realtime: ¿Es relevante?', esRelevante);
              console.log('useChatMessages realtime: Condición 1 (friend->user):', newMessage.remitente === friendId && newMessage.destinatario === session.user.id);
              console.log('useChatMessages realtime: Condición 2 (user->friend):', newMessage.remitente === session.user.id && newMessage.destinatario === friendId);
            }
            
            if (esRelevante) {
              if (DEBUG) {
                console.log('✅ useChatMessages realtime: MENSAJE RELEVANTE - Invalidando queries');
                console.log('useChatMessages realtime: Invalidando [\'chatMessages\',', friendId, ',', session.user.id, ']');
              }
              // Invalidar cache de mensajes Y lista de chatrooms
              debouncedInvalidate();
              
              // También invalidar chatrooms para actualizar último mensaje (de forma específica)
              if (typeof window !== 'undefined' && window.__REACT_QUERY_CLIENT__) {
                if (DEBUG) console.log('useChatMessages realtime: Invalidando chatrooms también');
                window.__REACT_QUERY_CLIENT__.invalidateQueries(['chatrooms']);
              }
            } else {
              if (DEBUG) {
                console.log('❌ useChatMessages realtime: Mensaje NO relevante para este chat');
                console.log('useChatMessages realtime: Mensaje entre:', newMessage?.remitente, '->', newMessage?.destinatario);
                console.log('useChatMessages realtime: Esperando mensajes entre:', session.user.id, '<->', friendId);
              }
            }
          } else {
            if (DEBUG) {
              console.log('⚠️ useChatMessages realtime: Faltan datos para verificar relevancia:');
              console.log('  - newMessage?.remitente:', !!newMessage?.remitente);
              console.log('  - newMessage?.destinatario:', !!newMessage?.destinatario);
              console.log('  - friendId:', !!friendId);
              console.log('  - session?.user?.id:', !!session?.user?.id);
            }
          }
        });
      }

      // Suscribirse al canal con manejo mejorado de errores
      subscribe((status, err) => {
        if (DEBUG) {
          console.log('=== useChatMessages: ESTADO DE SUSCRIPCIÓN ===');
          console.log('useChatMessages realtime: Estado:', status);
          console.log('useChatMessages realtime: Canal:', consistentChannelName);
          console.log('useChatMessages realtime: Tipo:', chatroomId ? `chatroom: ${chatroomId}` : 'canal genérico');
          console.log('useChatMessages realtime: Error:', err);
          console.log('useChatMessages realtime: Timestamp:', new Date().toISOString());
          
          if (status === 'SUBSCRIBED') {
            console.log('✅ useChatMessages realtime: SUSCRIPCIÓN ACTIVA');
          } else if (status === 'CHANNEL_ERROR') {
            console.log('❌ useChatMessages realtime: ERROR EN EL CANAL');
          } else if (status === 'TIMED_OUT') {
            console.log('⏰ useChatMessages realtime: TIEMPO DE ESPERA AGOTADO');
          } else if (status === 'CLOSED') {
            console.log('🔒 useChatMessages realtime: CANAL CERRADO');
          }
        }
        
        if (status === 'SUBSCRIBED') {
          if (DEBUG) console.log('useChatMessages realtime: Suscripción activa para', chatroomId ? `chatroom: ${chatroomId}` : 'canal genérico');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('useChatMessages realtime: Error en canal:', err);
          // Implementar reintento automático después de 5 segundos
          setTimeout(() => {
            if (DEBUG) {
              console.log('=== useChatMessages: REINTENTANDO SUSCRIPCIÓN ===');
              console.log('useChatMessages realtime: Reintentando suscripción...');
            }
            setupRealtimeSubscription();
          }, 5000);
        }
      });

    if (DEBUG) {
      console.log('=== useChatMessages: CONFIGURACIÓN COMPLETADA ===');
      console.log('useChatMessages setupRealtimeSubscription: Canal configurado exitosamente para', chatroomId ? `chatroom: ${chatroomId}` : 'canal genérico');
      console.log('useChatMessages setupRealtimeSubscription: Timestamp:', new Date().toISOString());
    }

    } catch (error) {
      console.error('useChatMessages setupRealtimeSubscription: Error configurando suscripción:', error);
      if (DEBUG) {
        console.log('❌ useChatMessages setupRealtimeSubscription: ERROR EN CONFIGURACIÓN');
        console.log('useChatMessages setupRealtimeSubscription: Error details:', {
          message: error.message,
          stack: error.stack,
          friendId,
          sessionUserId: session?.user?.id,
          consistentChannelName,
          timestamp: new Date().toISOString()
        });
      }
    }
  }, [friendId, session?.user?.id, on, getChannelState, reset, subscribe, debouncedInvalidate, consistentChannelName]);

  // Efecto separado para configurar el canal solo cuando esté disponible
  // Evita reconfiguraciones innecesarias del canal
  useEffect(() => {
    if (!friendId || !session?.user?.id || !channel) {
      return;
    }

    // Verificar si ya está suscrito para evitar reconfiguración
    const channelState = getChannelState();
    if (channelState.isSubscribed) {
      return;
    }

    if (DEBUG) console.log('useChatMessages: Configurando suscripción del canal');
    setupRealtimeSubscription();

  }, [friendId, session?.user?.id, channel, getChannelState, setupRealtimeSubscription]);

  // Exponer información de debugging del canal
  useEffect(() => {
    if (channel && DEBUG) {
      const channelState = getChannelState();
      console.log('useChatMessages: Estado del canal:', channelState.debug?.getChannelInfo());
    }
  }, [channel, getChannelState]);

  // Cleanup para evitar memory leaks
  useEffect(() => {
    return () => {
      if (invalidateTimeout.current) {
        clearTimeout(invalidateTimeout.current);
      }
      if (DEBUG) console.log('useChatMessages: Cleanup completado');
    };
  }, []);

  return query;
}
